type SafeContextValue =
  | boolean
  | number
  | string
  | readonly (boolean | number | string)[]
  | undefined;

export type SafeClientErrorContext = Record<string, SafeContextValue>;

export interface ClientErrorReporter {
  addError: (error: unknown, context?: object) => void;
}

export interface NormalizedClientError {
  name: string;
  message: string;
  stack?: string;
}

const CLIENT_ERROR_SERVICE = 'descope-flows';
const DEFAULT_ERROR_MESSAGE = 'Unknown client error';
const MAX_CONTEXT_STRING_LENGTH = 300;
const MAX_ERROR_STRING_LENGTH = 1000;

const SAFE_CONTEXT_KEYS = new Set([
  'component',
  'componentStack',
  'errorBoundary',
  'flow',
  'flowId',
  'handler',
  'key',
  'language',
  'lng',
  'locale',
  'localizationMode',
  'mode',
  'namespace',
  'ns',
  'reason',
  'route',
  'screen',
  'screenName',
  'source',
  'status',
  'statusCode',
  'type',
  'variable',
]);

const UNSAFE_CONTEXT_KEY_PATTERN =
  /(account|auth|code|context|cookie|email|form|identifier|jwt|login|otp|password|payload|phone|secret|session|token|user)/i;

let clientErrorReporter: ClientErrorReporter | undefined;
let consoleFallbackEnabled = true;
let clientErrorContext: SafeClientErrorContext = {};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const truncate = (value: string, maxLength: number): string =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const TRAILING_URL_PUNCTUATION_PATTERN = /[),.;\]]$/;

const redactUrlQueryString = (value: string): string => {
  let candidate = value;
  let trailingPunctuation = '';

  while (TRAILING_URL_PUNCTUATION_PATTERN.test(candidate)) {
    trailingPunctuation = `${candidate.slice(-1)}${trailingPunctuation}`;
    candidate = candidate.slice(0, -1);
  }

  try {
    const url = new URL(candidate);
    const hadSearch = url.search.length > 0;
    const safeHashRoute = url.hash ? (url.hash.split('?')[0] ?? '') : '';

    return `${url.origin}${url.pathname}${
      hadSearch ? '?[REDACTED_QUERY]' : ''
    }${safeHashRoute}${trailingPunctuation}`;
  } catch {
    return value;
  }
};

const redactUrlQueryStrings = (value: string): string =>
  value.replace(URL_IN_TEXT_PATTERN, redactUrlQueryString);

export const redactClientErrorString = (value: string): string => {
  return redactUrlQueryStrings(value)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
    .replace(
      /\b(maskedEmail)\s*[:=]\s*["']?[^"'\s\]]+/gi,
      '$1=[REDACTED_EMAIL]'
    )
    .replace(/\bsuggestions:\[[^\]]*]/gi, 'suggestions:[REDACTED]')
    .replace(/\+?\d[\d\s().-]{7,}\d/g, '[REDACTED_PHONE]')
    .replace(
      /\b(otp|code|verification_code|passcode)\s*[:=]\s*["']?[A-Za-z0-9_-]{4,}["']?/gi,
      '$1=[REDACTED_CODE]'
    )
    .replace(/\b\d{6}\b/g, '[REDACTED_CODE]')
    .replace(
      /\b(token|access_token|refresh_token|id_token|jwt|authorization|secret|session)\s*[:=]\s*["']?[^"'\s&,;]+/gi,
      '$1=[REDACTED_TOKEN]'
    )
    .replace(
      /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
      '[REDACTED_TOKEN]'
    );
};

const sanitizeString = (value: string, maxLength: number): string =>
  truncate(redactClientErrorString(value), maxLength);

const sanitizeContextValue = (value: unknown): SafeContextValue => {
  if (typeof value === 'string') {
    return sanitizeString(value, MAX_CONTEXT_STRING_LENGTH);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    const safeValues = value
      .filter(
        item =>
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
      )
      .map(item =>
        typeof item === 'string'
          ? sanitizeString(item, MAX_CONTEXT_STRING_LENGTH)
          : item
      );

    return safeValues.length ? safeValues : undefined;
  }

  return undefined;
};

export const sanitizeClientErrorContext = (
  context: unknown
): SafeClientErrorContext => {
  if (!isRecord(context)) {
    return {};
  }

  return Object.entries(context).reduce<SafeClientErrorContext>(
    (safeContext, [key, value]) => {
      if (!SAFE_CONTEXT_KEYS.has(key) || UNSAFE_CONTEXT_KEY_PATTERN.test(key)) {
        return safeContext;
      }

      const safeValue = sanitizeContextValue(value);
      if (safeValue !== undefined) {
        safeContext[key] = safeValue;
      }

      return safeContext;
    },
    {}
  );
};

export const sanitizeClientErrorUrl = (value: string): string => {
  try {
    const baseUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://linktr.ee';
    const url = new URL(value, baseUrl);
    const safeHashRoute = url.hash ? (url.hash.split('?')[0] ?? '') : '';

    url.search = '';
    url.hash = safeHashRoute;

    return url.toString();
  } catch {
    return sanitizeString(value, MAX_CONTEXT_STRING_LENGTH);
  }
};

const getSafeRoute = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const hashRoute = window.location.hash.split('?')[0];
  return sanitizeString(`${window.location.pathname}${hashRoute}`, 200);
};

const getDocumentLocale = (): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  return document.documentElement.lang || undefined;
};

const getRuntimeEnvValue = (key: keyof ImportMetaEnv): string | undefined => {
  const value = import.meta.env[key];
  return value || undefined;
};

const getBaseContext = (): SafeClientErrorContext => ({
  service: CLIENT_ERROR_SERVICE,
  env:
    getRuntimeEnvValue('VITE_DATADOG_ENV') ??
    getRuntimeEnvValue('VITE_STATSIG_STAGE') ??
    import.meta.env.MODE,
  version: getRuntimeEnvValue('VITE_BUILD_VERSION') ?? 'local',
  route: getSafeRoute(),
  locale: getDocumentLocale(),
  ...clientErrorContext,
});

const hasErrorLikeFields = (value: Record<string, unknown>): boolean =>
  ['message', 'name', 'stack', 'error', 'detail', 'type'].some(key =>
    Object.prototype.hasOwnProperty.call(value, key)
  );

const isContextOnlyPayload = (value: unknown): boolean =>
  isRecord(value) && !hasErrorLikeFields(value);

const getStringField = (
  value: Record<string, unknown>,
  key: string
): string | undefined =>
  typeof value[key] === 'string' ? (value[key] as string) : undefined;

export const normalizeClientError = (
  value: unknown,
  label?: string
): NormalizedClientError => {
  if (value instanceof Error) {
    const message = label
      ? `${label} ${value.message || DEFAULT_ERROR_MESSAGE}`
      : value.message || DEFAULT_ERROR_MESSAGE;

    return {
      name: value.name || 'Error',
      message: sanitizeString(message, MAX_ERROR_STRING_LENGTH),
      stack: value.stack
        ? sanitizeString(value.stack, MAX_ERROR_STRING_LENGTH * 4)
        : undefined,
    };
  }

  if (typeof value === 'string') {
    return {
      name: 'Error',
      message: sanitizeString(
        label ? `${label} ${value}` : value,
        MAX_ERROR_STRING_LENGTH
      ),
    };
  }

  if (isRecord(value)) {
    const name =
      getStringField(value, 'name') ??
      (value.constructor?.name && value.constructor.name !== 'Object'
        ? value.constructor.name
        : 'NonErrorObject');
    const message =
      getStringField(value, 'message') ??
      getStringField(value, 'error') ??
      getStringField(value, 'detail') ??
      getStringField(value, 'type') ??
      DEFAULT_ERROR_MESSAGE;
    const stack = getStringField(value, 'stack');

    return {
      name: sanitizeString(name, MAX_CONTEXT_STRING_LENGTH),
      message: sanitizeString(
        label ? `${label} ${message}` : message,
        MAX_ERROR_STRING_LENGTH
      ),
      stack: stack
        ? sanitizeString(stack, MAX_ERROR_STRING_LENGTH * 4)
        : undefined,
    };
  }

  return {
    name: 'Error',
    message: sanitizeString(
      label ? `${label} ${String(value)}` : String(value),
      MAX_ERROR_STRING_LENGTH
    ),
  };
};

const toReportableError = (normalizedError: NormalizedClientError): Error => {
  const error = new Error(normalizedError.message);
  error.name = normalizedError.name;

  if (normalizedError.stack) {
    error.stack = normalizedError.stack;
  }

  return error;
};

const buildClientErrorReport = (
  data: unknown[]
): { error: Error; context: SafeClientErrorContext } => {
  const label = typeof data[0] === 'string' ? data[0] : undefined;
  const rest = label ? data.slice(1) : data;
  const explicitContextPayload =
    rest.length > 1 &&
    isRecord(rest[rest.length - 1]) &&
    !(rest[rest.length - 1] instanceof Error)
      ? rest[rest.length - 1]
      : undefined;
  const errorCandidates = explicitContextPayload ? rest.slice(0, -1) : rest;
  const contextPayloads = [
    ...errorCandidates.filter(isContextOnlyPayload),
    ...(explicitContextPayload ? [explicitContextPayload] : []),
  ];
  const errorInput =
    errorCandidates.find(value => !isContextOnlyPayload(value)) ??
    label ??
    DEFAULT_ERROR_MESSAGE;
  const normalizedError = normalizeClientError(
    errorInput,
    errorInput === label ? undefined : label
  );
  const context = contextPayloads.reduce<SafeClientErrorContext>(
    (safeContext, contextPayload) => ({
      ...safeContext,
      ...sanitizeClientErrorContext(contextPayload),
    }),
    {
      ...getBaseContext(),
      errorName: normalizedError.name,
      errorMessage: normalizedError.message,
    }
  );

  return { error: toReportableError(normalizedError), context };
};

export const setClientErrorReporter = (
  reporter: ClientErrorReporter | undefined
): void => {
  clientErrorReporter = reporter;
};

export const setClientErrorContext = (
  context: SafeClientErrorContext
): void => {
  clientErrorContext = {
    ...clientErrorContext,
    ...sanitizeClientErrorContext(context),
  };
};

export const reportClientError = (...data: unknown[]): void => {
  const { error, context } = buildClientErrorReport(data);

  if (clientErrorReporter) {
    try {
      clientErrorReporter.addError(error, context);
      return;
    } catch (reporterError) {
      if (consoleFallbackEnabled) {
        // eslint-disable-next-line no-console
        console.error('Client error reporter failed:', reporterError);
      }
    }
  }

  if (consoleFallbackEnabled) {
    // eslint-disable-next-line no-console
    console.error(...data);
  }
};

export const resetClientErrorReportingForTests = (): void => {
  clientErrorReporter = undefined;
  consoleFallbackEnabled = true;
  clientErrorContext = {};
};

export const setClientErrorConsoleFallbackForTests = (
  enabled: boolean
): void => {
  consoleFallbackEnabled = enabled;
};
